import { currency, decimal, type CurrencyCode } from '@/shared/lib/format';
import type { BenefitLineDef, OperatorProgram } from './operator-rules';
import { catalogCost, type CatalogProduct } from './operator-payload';
import { parseMaskedMoney } from './operator-money';

/** Valida se o valor/produto de resgate ultrapassa o disponível (inclui teto cashLimit). */
export function getRedemptionExcessError(opts: {
  benefitLines: BenefitLineDef[];
  benefitState: Record<string, boolean>;
  redemptionAmount: string;
  redemptionValue: number;
  selectedProduct?: CatalogProduct | null;
  balance: number;
  currencyCode?: CurrencyCode;
  program?: OperatorProgram;
  /** passbook.usesProductValueFieldAsDiscount — value vira R$ em produtos discount, não custo. */
  usesValueAsDiscount?: boolean;
  /** passbook.maxInputValidation — custo em selos do resgate quando usesValueAsDiscount. */
  maxInputValidation?: number;
}): string | null {
  const {
    benefitLines,
    benefitState,
    redemptionAmount,
    redemptionValue,
    selectedProduct,
    balance,
    currencyCode = 'BRL',
    program = 'points',
    usesValueAsDiscount,
    maxInputValidation,
  } = opts;

  if (benefitState.balance) {
    const line = benefitLines.find((l) => l.id === 'balance');
    if (!line?.eligible || line.maxAmount == null) return null;

    if (line.kind === 'cash') {
      const typed = parseMaskedMoney(redemptionAmount);
      if (typed > line.maxAmount + 1e-9) {
        return `Valor acima do disponível (${currency(line.maxAmount, currencyCode)}).`;
      }
    }

    if (line.kind === 'points') {
      const typed = Number(redemptionAmount) || 0;
      if (typed > line.maxAmount) {
        return `Pontos acima do disponível (${decimal(line.maxAmount)}).`;
      }
    }

    if (line.kind === 'stamps' && redemptionValue > line.maxAmount) {
      return `Selos acima do disponível (${decimal(line.maxAmount)}).`;
    }
  }

  if (benefitState.catalog && selectedProduct) {
    const cost = catalogCost(selectedProduct, {
      usesProductValueFieldAsDiscount: usesValueAsDiscount,
      maxInputValidation,
    });
    const cashLike = program === 'cashback' || program === 'giftcard' || program === 'balance';
    const fmt = (n: number) => (cashLike ? currency(n, currencyCode) : `${decimal(n)} pts`);

    if (cost > balance) {
      return `Produto exige ${fmt(cost)} — saldo disponível: ${fmt(balance)}.`;
    }
    const catalogLine = benefitLines.find((l) => l.id === 'catalog');
    if (catalogLine?.maxAmount != null && cost > catalogLine.maxAmount) {
      return `Produto acima do limite de uso (${fmt(catalogLine.maxAmount)}).`;
    }
  }

  return null;
}
